import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Logo } from '../../layout';
import { AlphaBadge } from '../../common';
import AdminBar from './AdminBar';
import HeaderUserMenu from './HeaderUserMenu';
import './Header.css';
import { useMediaQuery, Tooltip, IconButton } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { analytics } from '../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import InstrumentSearch from '../../MobileView/components/InstrumentSearch';
import FeedbackModal from '../../common/FeedbackModal';
import { useFeedbackEnabled } from '../../../context/FeatureFlagContext';

const Header = ({
  isDarkMode,
  selectedCategory,
  selectedInstrument,
  instruments,
  onCategoryChange,
  onInstrumentChange,
  searchQuery,
  autoCompleteOptions,
  onSelect,
  onSearch,
  isPremium,
  isInstrumentLoading = false,
  isCategoryLoading = false,
  shouldHighlightInstrumentSelector = false,
  isPanelsSwitched = false,
  onTogglePanelsSwitch,
  onSearchActiveChange,
  conceptGatePanelVisible = true,
  onToggleConceptGatePanel,
  chatPanelVisible = true,
  onToggleChatPanel,
  chatEnabled = true,
  // User menu props
  onDarkModeChange,
  onLogout,
  onShowRulesHelp,
  onStartTour,
  onToggleJournal,
}) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const userInfo = useSelector((state) => state.user.userInfo);
  const isAdmin = userInfo?.admin || false;
  const [isAdminBarVisible, setIsAdminBarVisible] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const isFeedbackEnabled = useFeedbackEnabled();

  const tooltipContent = (
    <div style={{ padding: '8px', maxWidth: '300px', textAlign: 'left' }}>
      <p style={{ margin: '0 0 8px 0' }}>This is an experimental version of NanoTradingCopilot that:</p>
      <ul style={{ margin: '0', paddingLeft: '20px' }}>
        <li>May contain bugs or incomplete features</li>
        <li>Could have frequent updates and changes</li>
        <li>May have limited functionality</li>
        <li>Is being actively developed and improved</li>
      </ul>
    </div>
  );

  const handleSearch = (value) => {
    onSearch(value);

    // Log search event
    logEvent(analytics, 'search_performed', {
      search_query: value,
      category: selectedCategory,
      results_count: autoCompleteOptions.length,
      has_results: autoCompleteOptions.length > 0
    });
  };

  const handleInstrumentSelect = (value, category) => {
    onSelect(value, category);

    // Log instrument selection from search
    logEvent(analytics, 'instrument_selected_from_search', {
      instrument: value,
      category: category || selectedCategory,
      provider: instruments[category || selectedCategory]?.[value]?.provider || 'unknown',
      search_query: searchQuery
    });
  };

  return (
    <>
      <div className={`main-header ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="header-left">
          <Logo size="30px" />
          {isMobile ? (
            <Tooltip
              title={tooltipContent}
              arrow
              placement="bottom"
            >
              <span className="alpha-badge-mobile">α</span>
            </Tooltip>
          ) : (
            <AlphaBadge style={{ marginLeft: '12px', fontSize: '0.75em' }} />
          )}
        </div>
        <div className="header-controls">
          <InstrumentSearch
            instruments={instruments}
            autoCompleteOptions={autoCompleteOptions}
            selectedInstrument={selectedInstrument}
            selectedCategory={selectedCategory}
            onSelectInstrument={handleInstrumentSelect}
            onSearch={handleSearch}
            isDarkMode={isDarkMode}
            variant={isMobile ? 'mobile' : 'desktop'}
            shouldHighlight={shouldHighlightInstrumentSelector}
            className="header-instrument-search"
            defaultCategory={selectedCategory}
            onSearchActiveChange={onSearchActiveChange}
          />

          {onToggleConceptGatePanel && (
            <Tooltip title={conceptGatePanelVisible ? "Hide validation panel" : "Show validation panel"} arrow>
              <IconButton
                onClick={onToggleConceptGatePanel}
                className={`conceptgate-toggle-button ${conceptGatePanelVisible ? 'active' : ''}`}
                size="small"
                aria-label={conceptGatePanelVisible ? "Hide validation panel" : "Show validation panel"}
              >
                <FactCheckOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {chatEnabled && onToggleChatPanel && (
            <Tooltip title={chatPanelVisible ? "Hide chat" : "Show chat"} arrow>
              <IconButton
                onClick={onToggleChatPanel}
                className={`chat-toggle-button ${chatPanelVisible ? 'active' : ''}`}
                size="small"
                aria-label={chatPanelVisible ? "Hide chat" : "Show chat"}
              >
                <ChatOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {onTogglePanelsSwitch && (
            <Tooltip title={isPanelsSwitched ? "Switch to chart in middle" : "Switch to validation in middle"} arrow>
              <IconButton
                onClick={onTogglePanelsSwitch}
                className={`panel-switch-button ${isPanelsSwitched ? 'switched' : ''}`}
                size="small"
                aria-label={isPanelsSwitched ? "Switch to chart in middle" : "Switch to validation in middle"}
              >
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>
          )}

          {isFeedbackEnabled && (
            <Tooltip title="Send feedback" arrow>
              <IconButton
                onClick={() => setIsFeedbackModalOpen(true)}
                className="feedback-button"
                size="small"
                aria-label="Send feedback"
              >
                <FeedbackOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {isAdmin && (
            <Tooltip title={isAdminBarVisible ? "Hide admin menu" : "Show admin menu"} arrow>
              <IconButton
                onClick={() => setIsAdminBarVisible(!isAdminBarVisible)}
                className={`admin-toggle-button ${isAdminBarVisible ? 'active' : ''}`}
                size="small"
                aria-label={isAdminBarVisible ? "Hide admin menu" : "Show admin menu"}
              >
                <TuneIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* User Menu - Desktop only */}
          {!isMobile && onLogout && (
            <div className="header-right">
              <HeaderUserMenu
                isDarkMode={isDarkMode}
                onDarkModeChange={onDarkModeChange}
                onLogout={onLogout}
                onShowRulesHelp={onShowRulesHelp}
                onStartTour={onStartTour}
                onToggleJournal={onToggleJournal}
              />
            </div>
          )}
        </div>
      </div>
      {isAdmin && isAdminBarVisible && <AdminBar isDarkMode={isDarkMode} isAdmin={isAdmin} />}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </>
  );
};

export default Header; 
